import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Lightbox = ({ media, currentIndex, onClose, onNavigate }) => {
  const currentMedia = media[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < media.length - 1;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && hasPrev) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && hasNext) onNavigate(currentIndex + 1);
  }, [onClose, onNavigate, currentIndex, hasPrev, hasNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  if (!currentMedia) return null;

  const mediaUrl = currentMedia.url?.startsWith('http') 
    ? currentMedia.url 
    : `${BACKEND_URL}${currentMedia.url}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox-overlay"
      >
        {/* Close button */}
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
        >
          <X className="w-6 h-6" strokeWidth={1.5} />
        </button>

        {/* Navigation - Previous */}
        {hasPrev && (
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(currentIndex - 1);
            }}
            className="absolute left-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
          >
            <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
          </button>
        )}

        {/* Navigation - Next */}
        {hasNext && (
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(currentIndex + 1);
            }}
            className="absolute right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
          >
            <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
          </button>
        )}

        {/* Media content */}
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {currentMedia.content_type?.startsWith('image/') ? (
            <img
              data-testid="lightbox-image"
              src={mediaUrl}
              alt="Full view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          ) : (
            <video
              data-testid="lightbox-video"
              src={mediaUrl}
              controls
              autoPlay
              className="max-w-full max-h-[90vh] rounded-lg"
            />
          )}
        </motion.div>

        {/* Image counter */}
        {media.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 text-white px-4 py-2 rounded-full text-sm">
            {currentIndex + 1} / {media.length}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default Lightbox;
